import { useState, useEffect, useRef, useCallback } from 'react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import {
  listFreeReceiptsByPo,
  receiveFreePo,
  patchFreeReceipt,
  voidFreeReceipt,
  type FreeReceiptDto,
  type FreePoDetail,
} from '@/services/procurement/freePoApi'
import { parseNumberVi, normalizeServerNumber } from '@/components/table/freeSheetModel'
import { formatDateFull } from '@/lib/dateFormat'
import { CheckCircle2, AlertCircle, RefreshCw, Edit2, Ban } from 'lucide-react'

interface FreePoReceiveTabProps {
  poId: string
  poDto: FreePoDetail | any
  onUpdated: () => void | Promise<void>
}

const formatNumber = (n: number | string | null | undefined): string => {
  if (n == null || n === '') return '0'
  const num = normalizeServerNumber(n)
  if (num == null || !Number.isFinite(num)) return '0'
  return new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 4 }).format(num)
}

export function FreePoReceiveTab({ poId, poDto, onUpdated }: FreePoReceiveTabProps) {
  const freeLines: any[] = poDto?.free_lines || []
  const poStatus = poDto?.status || 'DRAFT'
  const isSentOrPartial = poStatus === 'SENT' || poStatus === 'PARTIAL'

  // Receipts history state
  const [receipts, setReceipts] = useState<FreeReceiptDto[]>([])
  const [loadingReceipts, setLoadingReceipts] = useState(true)
  const [receiptsError, setReceiptsError] = useState<string | null>(null)

  // Receive form state
  const [recvQty, setRecvQty] = useState<Record<string, string>>({})
  const [recvNote, setRecvNote] = useState('')
  const [submittingReceive, setSubmittingReceive] = useState(false)
  const [receiveError, setReceiveError] = useState<string | null>(null)
  const [receiveSuccess, setReceiveSuccess] = useState<string | null>(null)
  const lastKeyRef = useRef<string>('')
  const [lastFailedKey, setLastFailedKey] = useState<string>('')
  const [hasNetworkError, setHasNetworkError] = useState(false)

  // Edit receipt modal state
  const [editModal, setEditModal] = useState<{
    receipt: FreeReceiptDto
    lines: Record<string, string>
    reason: string
  } | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Void receipt modal state
  const [voidModal, setVoidModal] = useState<{
    receipt: FreeReceiptDto
    reason: string
  } | null>(null)
  const [savingVoid, setSavingVoid] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)

  // Initialize recvQty to remaining amounts
  useEffect(() => {
    const initial: Record<string, string> = {}
    for (const fl of freeLines) {
      const q = normalizeServerNumber(fl.qty) ?? 0
      const r = normalizeServerNumber(fl.received_qty) ?? 0
      const rem = Math.max(0, Math.round((q - r) * 10000) / 10000)
      initial[fl.id] = rem > 0 ? String(rem) : '0'
    }
    setRecvQty(initial)
  }, [freeLines])

  // Load receipts list
  const loadReceipts = useCallback(async () => {
    setLoadingReceipts(true)
    setReceiptsError(null)
    try {
      const data = await listFreeReceiptsByPo(poId)
      setReceipts(data)
    } catch (err: any) {
      setReceiptsError(err?.message || 'Không thể tải lịch sử phiếu nhận hàng')
    } finally {
      setLoadingReceipts(false)
    }
  }, [poId])

  useEffect(() => {
    void loadReceipts()
  }, [loadReceipts])

  // Receive submit
  const handleReceive = async (useExistingKey = false) => {
    setReceiveError(null)
    setReceiveSuccess(null)
    setHasNetworkError(false)

    const linesToReceive: Array<{ po_free_line_id: string; qty: number }> = []

    for (const fl of freeLines) {
      const valStr = recvQty[fl.id]?.trim() ?? ''
      if (!valStr || valStr === '0') continue
      const q = parseNumberVi(valStr)
      if (q === null || q <= 0) {
        setReceiveError(`Dòng "${fl.name}": Số lượng nhận phải là số dương lớn hơn 0`)
        return
      }

      const orderQty = Number(fl.qty || 0)
      const receivedQty = Number(fl.received_qty || 0)
      const remain = Math.max(0, Math.round((orderQty - receivedQty) * 10000) / 10000)

      if (q > remain + 1e-9) {
        setReceiveError(
          `Dòng "${fl.name}": Số lượng nhận (${formatNumber(q)}) vượt quá số lượng còn lại (${formatNumber(remain)})`,
        )
        return
      }

      linesToReceive.push({
        po_free_line_id: fl.id,
        qty: q,
      })
    }

    if (linesToReceive.length === 0) {
      setReceiveError('Vui lòng nhập số lượng nhận (> 0) cho ít nhất một dòng')
      return
    }

    // Determine Idempotency-Key
    let key = lastKeyRef.current
    if (!useExistingKey || !key) {
      key = `frk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
      lastKeyRef.current = key
    }

    setSubmittingReceive(true)
    try {
      const res = await receiveFreePo(
        poId,
        {
          lines: linesToReceive,
          note: recvNote.trim() || null,
        },
        key,
      )

      setReceiveSuccess(`Đã ghi nhận phiếu nhận hàng thành công (Mã: ${res.receipt.code})`)
      setRecvNote('')
      setHasNetworkError(false)
      await Promise.all([loadReceipts(), Promise.resolve(onUpdated())])
    } catch (err: any) {
      console.error('Failed to receive free PO items:', err)
      setReceiveError(err?.message || 'Có lỗi xảy ra khi ghi nhận nhận hàng')
      setLastFailedKey(key)
      if (err?.name === 'TypeError' || err?.message?.includes('fetch') || err?.status === 0) {
        setHasNetworkError(true)
      }
    } finally {
      setSubmittingReceive(false)
    }
  }

  // Open Edit Modal
  const handleOpenEditModal = (rcpt: FreeReceiptDto) => {
    const linesMap: Record<string, string> = {}
    for (const l of rcpt.lines) {
      linesMap[l.id] = String(l.qty)
    }
    setEditModal({
      receipt: rcpt,
      lines: linesMap,
      reason: '',
    })
    setEditError(null)
  }

  // Save Edit
  const handleSaveEdit = async () => {
    if (!editModal) return
    setEditError(null)

    if (!editModal.reason.trim()) {
      setEditError('Lý do sửa là bắt buộc')
      return
    }

    const payloadLines: Array<{ id: string; qty: number }> = []
    for (const l of editModal.receipt.lines) {
      const valStr = editModal.lines[l.id]?.trim() ?? ''
      const q = parseNumberVi(valStr)
      if (q === null || q <= 0) {
        setEditError('Số lượng nhận phải lớn hơn 0')
        return
      }
      payloadLines.push({ id: l.id, qty: q })
    }

    setSavingEdit(true)
    try {
      await patchFreeReceipt(editModal.receipt.id, {
        lines: payloadLines,
        reason: editModal.reason.trim(),
      })
      setEditModal(null)
      await Promise.all([loadReceipts(), Promise.resolve(onUpdated())])
    } catch (err: any) {
      setEditError(err?.message || 'Không thể sửa phiếu nhận hàng')
    } finally {
      setSavingEdit(false)
    }
  }

  // Open Void Modal
  const handleOpenVoidModal = (rcpt: FreeReceiptDto) => {
    setVoidModal({
      receipt: rcpt,
      reason: '',
    })
    setVoidError(null)
  }

  // Save Void
  const handleSaveVoid = async () => {
    if (!voidModal) return
    setVoidError(null)

    if (!voidModal.reason.trim()) {
      setVoidError('Lý do hủy phiếu là bắt buộc')
      return
    }

    setSavingVoid(true)
    try {
      await voidFreeReceipt(voidModal.receipt.id, voidModal.reason.trim())
      setVoidModal(null)
      await Promise.all([loadReceipts(), Promise.resolve(onUpdated())])
    } catch (err: any) {
      setVoidError(err?.message || 'Không thể hủy phiếu nhận hàng')
    } finally {
      setSavingVoid(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* SECTION 1: Nhận hàng */}
      <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Nhập nhận hàng vào kho tự do</h3>
            <p className="text-xs text-slate-500">
              Nhập số lượng thực nhận cho từng dòng hàng. Hàng sẽ được lưu riêng vào Kho hàng tự do mà không tạo vật tư.
            </p>
          </div>
          {isSentOrPartial ? (
            <Badge tone="info" className="text-xs">
              Sẵn sàng nhận hàng
            </Badge>
          ) : (
            <Badge tone="neutral" className="text-xs">
              PO chưa gửi hoặc đã hoàn tất ({poStatus})
            </Badge>
          )}
        </div>

        {receiveSuccess ? (
          <div className="mb-3 flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
            <span>{receiveSuccess}</span>
          </div>
        ) : null}

        {receiveError ? (
          <div className="mb-3 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900 space-y-2">
            <div className="flex items-center gap-1.5 font-semibold">
              <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
              <span>Lỗi nhận hàng:</span>
            </div>
            <div>{receiveError}</div>
            {hasNetworkError && (
              <div className="pt-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleReceive(true)}
                  disabled={submittingReceive}
                >
                  <RefreshCw className="mr-1 h-3.5 w-3.5" />
                  Gửi lại với cùng khóa Idempotency ({lastFailedKey})
                </Button>
              </div>
            )}
          </div>
        ) : null}

        {/* Bảng nhập số lượng nhận */}
        <div className="overflow-x-auto rounded border border-slate-200">
          <table className="w-full text-left text-xs text-slate-800">
            <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200">
              <tr>
                <th className="w-12 px-2.5 py-2 text-center">STT</th>
                <th className="px-3 py-2">Tên hàng</th>
                <th className="w-20 px-3 py-2 text-center">ĐVT</th>
                <th className="w-28 px-3 py-2 text-right">SL Đặt</th>
                <th className="w-28 px-3 py-2 text-right">Đã nhận</th>
                <th className="w-28 px-3 py-2 text-right">Còn lại</th>
                <th className="w-48 px-3 py-2 text-center">SL Nhận</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {freeLines.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-slate-400">
                    Không có dòng hàng nào trong PO.
                  </td>
                </tr>
              ) : (
                freeLines.map((fl, idx) => {
                  const orderQty = normalizeServerNumber(fl.qty) ?? 0
                  const receivedQty = normalizeServerNumber(fl.received_qty) ?? 0
                  const remain = Math.max(0, Math.round((orderQty - receivedQty) * 10000) / 10000)
                  const isDone = remain <= 0

                  return (
                    <tr key={fl.id} className={isDone ? 'bg-slate-50/50' : 'hover:bg-slate-50/80'}>
                      <td className="px-2.5 py-2 text-center text-slate-500 font-mono">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {fl.name}
                        {fl.note ? (
                          <div className="text-[11px] text-slate-400 font-normal italic">
                            {fl.note}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-center text-slate-600">{fl.uom}</td>
                      <td className="px-3 py-2 text-right font-medium">{formatNumber(orderQty)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-medium">
                        {formatNumber(receivedQty)}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-amber-700">
                        {formatNumber(remain)}
                      </td>
                      <td className="px-3 py-2">
                        {isSentOrPartial && !isDone ? (
                          <div className="flex items-center justify-center gap-1.5">
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`SL nhận dòng ${idx + 1}`}
                              value={recvQty[fl.id] ?? ''}
                              onChange={(e) => {
                                const val = e.target.value
                                setRecvQty((prev) => ({ ...prev, [fl.id]: val }))
                              }}
                              onWheel={(e) => e.currentTarget.blur()}
                              className="h-7 w-24 rounded border border-slate-300 px-2 text-right font-mono text-xs focus:outline-none focus:ring-1 focus:ring-sky-500"
                              placeholder="0"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setRecvQty((prev) => ({ ...prev, [fl.id]: String(remain) }))
                              }}
                              title="Điền tất cả số lượng còn lại"
                              className="rounded border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors cursor-pointer"
                            >
                              Tất cả
                            </button>
                          </div>
                        ) : (
                          <div className="text-center text-slate-400 italic">
                            {isDone ? 'Đã đủ' : '—'}
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {isSentOrPartial && freeLines.some((fl) => (normalizeServerNumber(fl.qty) ?? 0) > (normalizeServerNumber(fl.received_qty) ?? 0)) ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
            <div className="flex-1 min-w-[240px] max-w-md">
              <input
                type="text"
                placeholder="Ghi chú phiếu nhận hàng (nếu có)"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={recvNote}
                onChange={(e) => setRecvNote(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="primary"
                onClick={() => handleReceive(false)}
                disabled={submittingReceive}
              >
                {submittingReceive ? 'Đang ghi nhận...' : 'Nhận hàng'}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* SECTION 2: Lịch sử phiếu nhận hàng */}
      <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-[var(--shadow-level-1)]">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Lịch sử phiếu nhận hàng</h3>
            <p className="text-xs text-slate-500">
              Danh sách các phiếu nhận hàng đã phát sinh cho PO này. Có thể sửa số lượng hoặc hủy phiếu kèm lý do.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={loadReceipts}
            disabled={loadingReceipts}
          >
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loadingReceipts ? 'animate-spin' : ''}`} />
            Làm mới
          </Button>
        </div>

        {receiptsError ? (
          <div className="mb-3 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {receiptsError}
          </div>
        ) : null}

        {loadingReceipts ? (
          <div className="py-6 text-center text-xs text-slate-400">Đang tải lịch sử phiếu nhận...</div>
        ) : receipts.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400">
            Chưa có phiếu nhận hàng nào được tạo cho PO này.
          </div>
        ) : (
          <div className="space-y-3">
            {receipts.map((rcpt, rIdx) => {
              const isVoid = rcpt.status === 'VOID'

              return (
                <div
                  key={rcpt.id}
                  className={`rounded border transition-colors ${
                    isVoid ? 'border-red-200 bg-red-50/20' : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/60 px-3 py-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-slate-700">#{rIdx + 1}</span>
                      <span className="font-mono font-bold text-sky-800">{rcpt.code}</span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-500">
                        {formatDateFull(rcpt.received_at)}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="text-slate-600">Bởi: {rcpt.received_by}</span>
                      {rcpt.note ? (
                        <>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-500 italic">"{rcpt.note}"</span>
                        </>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {isVoid ? (
                        <div className="flex items-center gap-1.5">
                          <Badge tone="danger" className="text-xs">
                            Đã hủy
                          </Badge>
                          {rcpt.void_reason ? (
                            <span className="text-[11px] text-red-700 italic">
                              (Lý do: {rcpt.void_reason})
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <>
                          <Badge tone="success" className="text-xs">
                            Hợp lệ
                          </Badge>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleOpenEditModal(rcpt)}
                          >
                            <Edit2 className="mr-1 h-3 w-3" />
                            Sửa số lượng
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleOpenVoidModal(rcpt)}
                          >
                            <Ban className="mr-1 h-3 w-3 text-red-600" />
                            Hủy phiếu
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Lines list in receipt */}
                  <div className="p-2">
                    <table className="w-full text-xs text-left">
                      <thead className="text-slate-500 text-[11px]">
                        <tr>
                          <th className="w-10 px-2 py-1 text-center">STT</th>
                          <th className="px-2 py-1">Tên hàng</th>
                          <th className="w-20 px-2 py-1 text-center">ĐVT</th>
                          <th className="w-28 px-2 py-1 text-right">SL Nhận</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rcpt.lines.map((l, lIdx) => (
                          <tr key={l.id} className={isVoid ? 'text-slate-400 line-through' : ''}>
                            <td className="px-2 py-1 text-center text-slate-400">{lIdx + 1}</td>
                            <td className="px-2 py-1 font-medium">{l.name || '—'}</td>
                            <td className="px-2 py-1 text-center text-slate-500">{l.uom || '—'}</td>
                            <td className="px-2 py-1 text-right font-semibold font-mono">
                              {formatNumber(l.qty)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Modal Sửa Số Lượng Phiếu Nhận */}
      {editModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-[var(--radius-md)] bg-white p-5 shadow-xl space-y-4">
            <h3 className="text-sm font-semibold text-slate-800">
              Sửa số lượng phiếu nhận {editModal.receipt.code}
            </h3>

            {editError ? (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {editError}
              </div>
            ) : null}

            <div className="space-y-2 max-h-60 overflow-y-auto">
              {editModal.receipt.lines.map((l, idx) => (
                <div key={l.id} className="flex items-center justify-between gap-3 text-xs border-b pb-2">
                  <div className="flex-1">
                    <span className="font-semibold text-slate-700 mr-2">{idx + 1}.</span>
                    <span>{l.name || 'Dòng ' + (idx + 1)}</span>
                    <span className="ml-1 text-slate-400">({l.uom})</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <label className="text-slate-500">Số lượng:</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="h-7 w-24 rounded border border-slate-300 px-2 text-right font-mono text-xs focus:ring-1 focus:ring-sky-500"
                      value={editModal.lines[l.id] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value
                        setEditModal((prev) =>
                          prev ? { ...prev, lines: { ...prev.lines, [l.id]: val } } : null,
                        )
                      }}
                      onWheel={(e) => e.currentTarget.blur()}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">
                Lý do sửa <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Nhập lý do điều chỉnh số lượng"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-sky-500"
                value={editModal.reason}
                onChange={(e) => {
                  const val = e.target.value
                  setEditModal((prev) => (prev ? { ...prev, reason: val } : null))
                }}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setEditModal(null)}
                disabled={savingEdit}
              >
                Hủy
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={handleSaveEdit}
                disabled={savingEdit}
              >
                {savingEdit ? 'Đang lưu...' : 'Xác nhận sửa'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Modal Hủy Phiếu Nhận */}
      {voidModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-[var(--radius-md)] bg-white p-5 shadow-xl space-y-4">
            <h3 className="text-sm font-semibold text-red-700">
              Hủy phiếu nhận hàng {voidModal.receipt.code}
            </h3>
            <p className="text-xs text-slate-600">
              Thao tác này sẽ hủy phiếu nhận và trừ lại số lượng tồn kho tự do tương ứng.
            </p>

            {voidError ? (
              <div className="rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                {voidError}
              </div>
            ) : null}

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-700">
                Lý do hủy phiếu <span className="text-red-500">*</span>
              </label>
              <textarea
                rows={3}
                placeholder="Nhập lý do hủy phiếu nhận hàng"
                className="w-full rounded border border-slate-300 px-3 py-1.5 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-500"
                value={voidModal.reason}
                onChange={(e) => {
                  const val = e.target.value
                  setVoidModal((prev) => (prev ? { ...prev, reason: val } : null))
                }}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setVoidModal(null)}
                disabled={savingVoid}
              >
                Đóng
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={handleSaveVoid}
                disabled={savingVoid}
              >
                {savingVoid ? 'Đang hủy...' : 'Xác nhận hủy phiếu'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
